import { findNikkeInCatalog } from '../../nikke-catalog/domain/nikkeCatalog.js';

const text = (value) => String(value ?? '').trim();

const POSITIVE_MAGNITUDE_EFFECTS = new Set([
  'StatAccuracyCircle',
  'StatChargeTime',
]);

export const EQUIPMENT_SLOT_LABELS = {
  head: '머리',
  torso: '몸통',
  arm: '팔',
  leg: '다리',
};

export const OVERLOAD_EFFECT_LABELS = {
  StatAtk: '공격력',
  StatDef: '방어력',
  StatAmmoLoad: '장탄 수',
  StatChargeDamage: '차지 대미지',
  StatChargeTime: '차지 속도',
  StatCritical: '크리티컬 확률',
  StatCriticalDamage: '크리티컬 대미지',
  StatHit: '명중률',
  StatAccuracyCircle: '명중률',
  IncElementDmg: '우월 코드 대미지',
};

export const OVERLOAD_EFFECT_PRIORITY = [
  'IncElementDmg',
  'StatAtk',
  'StatAmmoLoad',
  'StatCritical',
  'StatCriticalDamage',
];

function normalizeOverloadMagnitude(effectType, value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return Number.NaN;
  return POSITIVE_MAGNITUDE_EFFECTS.has(effectType) ? Math.abs(numeric) : numeric;
}

function normalizeFavoriteItemTid(tid) {
  return Math.max(0, Math.trunc(Number(tid) || 0));
}

export function isFavoriteItem(tid) {
  return Math.trunc(normalizeFavoriteItemTid(tid) / 100000) >= 2;
}

export function favoriteItemLabel(tid) {
  return isFavoriteItem(tid) ? '애장품' : '소장품';
}

export function prioritizeOverloadEffects(effects = []) {
  const priority = new Map(OVERLOAD_EFFECT_PRIORITY.map((effectType, index) => [effectType, index]));
  return [...effects].sort((left, right) => {
    const leftOrder = priority.get(left.effectType) ?? OVERLOAD_EFFECT_PRIORITY.length;
    const rightOrder = priority.get(right.effectType) ?? OVERLOAD_EFFECT_PRIORITY.length;
    if (leftOrder !== rightOrder) return leftOrder - rightOrder;
    return Math.abs(right.value) - Math.abs(left.value) || left.label.localeCompare(right.label, 'ko-KR');
  });
}

export function personalNikkeSuperiorCodeValue(personal = {}) {
  return (Array.isArray(personal?.overloadOptions) ? personal.overloadOptions : [])
    .filter((option) => String(option?.effectType || '') === 'IncElementDmg')
    .reduce((sum, option) => sum + (Number(option?.value) || 0), 0);
}

export function formatPersonalNikkeNumber(value, fallback = '-') {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric.toLocaleString('ko-KR') : fallback;
}

export function formatFavoriteItemStage(tid, level) {
  const itemTid = normalizeFavoriteItemTid(tid);
  const itemLevel = Math.min(15, Math.max(0, Math.trunc(Number(level) || 0)));
  if (!itemTid) return '없음';
  if (isFavoriteItem(itemTid)) return 'SR15';
  const rarity = itemTid % 100 === 2 ? 'SR' : 'R';
  return `${rarity}${itemLevel}`;
}

export function formatOverloadValue(option = {}) {
  const effectType = text(option.effectType || option.function_type || option.functionType);
  const value = normalizeOverloadMagnitude(effectType, option.value ?? option.functionValue ?? option.function_value);
  if (!Number.isFinite(value)) return '-';
  const normalized = value / 100;
  const suffix = option.valueType === 'Percent' || option.valueType === 'Integer' ? '%' : '';
  return `${normalized.toFixed(2).replace(/\.00$/, '')}${suffix}`;
}

export function normalizeOverloadOption(option = {}) {
  const effectType = text(option.effectType || option.function_type || option.functionType);
  const value = normalizeOverloadMagnitude(effectType, option.value ?? option.function_value);
  return {
    id: text(option.id),
    effectType,
    label: OVERLOAD_EFFECT_LABELS[effectType] || text(option.label) || effectType || '옵션',
    value: Number.isFinite(value) ? value : 0,
    valueType: text(option.valueType || option.function_value_type),
    level: Number(option.level) || 0,
  };
}

export function normalizeEquipment(equipment = []) {
  if (!Array.isArray(equipment)) return [];
  return equipment.map((item) => ({
    slot: text(item.slot),
    label: text(item.label) || EQUIPMENT_SLOT_LABELS[text(item.slot)] || text(item.slot),
    tid: Number(item.tid) || 0,
    tier: Number(item.tier) || 0,
    level: Number(item.level) || 0,
    corporationType: Number(item.corporationType) || 0,
    overloadOptions: Array.isArray(item.overloadOptions)
      ? item.overloadOptions.map(normalizeOverloadOption).filter((option) => option.id)
      : [],
  }));
}

export function summarizeOverloadOptions(equipment = []) {
  const grouped = new Map();
  for (const item of normalizeEquipment(equipment)) {
    for (const option of item.overloadOptions) {
      const key = `${option.effectType || option.label}:${option.valueType}`;
      const previous = grouped.get(key);
      grouped.set(key, {
        effectType: option.effectType,
        label: option.label,
        value: (previous?.value || 0) + option.value,
        valueType: option.valueType,
        count: (previous?.count || 0) + 1,
      });
    }
  }
  return prioritizeOverloadEffects([...grouped.values()]);
}

export function normalizePersonalNikkeRow(row = {}, catalogItem = null) {
  const equipment = normalizeEquipment(row.equipment);
  const overloadOptions = equipment.flatMap((item) => item.overloadOptions.map((option) => ({
    ...option,
    slot: item.slot,
    slotLabel: item.label,
  })));
  return {
    userId: text(row.user_id),
    nameCode: text(row.name_code ?? row.nameCode),
    owned: row.owned !== false,
    level: Number(row.level) || 0,
    combat: Number(row.combat) || 0,
    arenaCombat: Number(row.arena_combat) || 0,
    core: Number(row.core) || 0,
    grade: Number(row.grade) || 0,
    costumeTid: Number(row.costume_tid) || 0,
    attractiveLevel: Number(row.attractive_level) || 0,
    skills: [
      Number(row.skill1_level) || 0,
      Number(row.skill2_level) || 0,
      Number(row.burst_skill_level) || 0,
    ],
    favoriteItemTid: Number(row.favorite_item_tid) || 0,
    favoriteItemLevel: Number(row.favorite_item_level) || 0,
    cubeTid: Number(row.cube_tid) || 0,
    cubeLevel: Number(row.cube_level) || 0,
    equipment,
    overloadOptions,
    overloadCount: overloadOptions.length,
    syncedAt: text(row.synced_at),
    catalogItem,
  };
}

export function buildPersonalNikkeLookup(rows = [], catalog = null) {
  const byNameCode = new Map();
  const byNikkeId = new Map();
  const all = rows.map((row) => {
    const nameCode = text(row.name_code ?? row.nameCode);
    const catalogItem = findNikkeInCatalog(catalog, { nameCode, sourceIdentifiers: row.sourceIdentifiers ?? row.source_identifiers }) || null;
    return normalizePersonalNikkeRow(row, catalogItem);
  });
  const items = all.filter((item) => item.overloadCount > 0);
  // The grid stays overload-only, but every synced row has to stay addressable: the squad and member
  // detail screens look one NIKKE up to show its skills, core, grade and favourite item, and those are
  // synced whether or not the gear carries overload lines. Dropping them from the maps made an owned
  // NIKKE read as not synced at all, and sorted it as unowned in the deck picker. Overloaded rows are
  // seeded first so an entry that resolved before still resolves to the same row.
  for (const item of [...items, ...all]) {
    if (item.nameCode && !byNameCode.has(item.nameCode)) byNameCode.set(item.nameCode, item);
    if (item.catalogItem?.nikkeId && !byNikkeId.has(item.catalogItem.nikkeId)) byNikkeId.set(item.catalogItem.nikkeId, item);
  }
  return { items, all, byNameCode, byNikkeId };
}

export function findPersonalNikke(lookup, catalogItem) {
  if (!lookup || !catalogItem) return null;
  const nameCode = text(catalogItem.nameCode);
  return (nameCode && lookup.byNameCode?.get(nameCode))
    || lookup.byNikkeId?.get(catalogItem.nikkeId)
    || null;
}
